import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr, refreshSession } from "@/lib/api";

const AuthContext = createContext(null);
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("nexreq-auth") : null;
const REFRESH_EVERY_MS = 20 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [offline, setOffline] = useState(false);
  const [ref, setRef] = useState(null);
  const lastRefresh = useRef(Date.now());

  const load = useCallback(async () => {
    // Emergent Google OAuth callback: exchange session_id before any /me check.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        window.history.replaceState(null, "", window.location.pathname);
        setUser(data.user);
        return;
      } catch {
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
    for (let attempt = 0; ; attempt += 1) {
      try {
        const { data } = await api.get("/auth/me");
        setOffline(false);
        setUser(data);
        return;
      } catch (e) {
        if (e?.response) {
          setOffline(false);
          setUser(false);
          return;
        }
        setOffline(true);
        await new Promise((resolve) => setTimeout(resolve, Math.min(10000, 1500 * (attempt + 1))));
      }
    }
  }, []);

  useEffect(() => { load(); }, [load, ref]);

  useEffect(() => {
    const expired = () => setUser(false);
    const onMessage = (event) => {
      if (event.data === "logout") setUser(false);
      if (event.data === "login") load();
    };
    window.addEventListener("nexreq:session-expired", expired);
    channel?.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("nexreq:session-expired", expired);
      channel?.removeEventListener("message", onMessage);
    };
  }, [load]);

  useEffect(() => {
    if (!user) return undefined;
    const renew = async () => {
      try {
        await refreshSession();
        lastRefresh.current = Date.now();
      } catch (e) {
        if (e?.response?.status === 401) setUser(false);
      }
    };
    const timer = setInterval(renew, REFRESH_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastRefresh.current > REFRESH_EVERY_MS / 2) renew();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const me = await api.get("/auth/me").then((r) => r.data).catch(() => data.user);
      setUser(me);
      lastRefresh.current = Date.now();
      channel?.postMessage("login");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiErr(e, "Invalid credentials") };
    }
  };

  const logout = async (reason) => {
    if (reason) {
      try { await api.post("/auth/session-events", { reason }); } catch {}
    }
    try { await api.post("/auth/logout"); } catch {}
    try { await api.post("/auth/google/logout"); } catch {}
    channel?.postMessage("logout");
    setUser(false);
  };

  const can = useCallback(
    (perm) => {
      const perms = user?.permissions || [];
      return perms.includes("*") || perms.includes(perm);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, offline, login, logout, can, refresh: () => setRef(Date.now()) }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
